import React, { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { Theme } from '@mui/material/styles';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Skeleton from '@mui/material/Skeleton';

import { CoreMultipleChoiceQuestionOption } from '../../types';

interface MultipleChoiceQuestionOptionPreviewProps {
  noColors?: boolean;
  option?: CoreMultipleChoiceQuestionOption;
  loading?: boolean;
}

const MultipleChoiceQuestionOptionPreview = (props: MultipleChoiceQuestionOptionPreviewProps) => {
  const { option, noColors, loading = false } = props;
  const intl = useIntl();

  const label = useMemo(() => {
    if (loading) {
      return <Skeleton width={180} />;
    }
    if (!option) {
      return intl.formatMessage({ id: 'noAnswerOption' });
    }
    return option.text;
  }, [loading, option, intl]);

  const color = useCallback(
    (theme: Theme) => {
      if (loading) {
        return theme.palette.grey[300];
      }
      if (noColors) {
        return theme.check.palette.exercise.color(undefined);
      }
      return theme.check.palette.exercise.color(!option ? 'none' : option?.type);
    },
    [noColors, loading, option]
  );

  return (
    <FormControlLabel
      disabled
      control={
        <Radio
          color="default"
          sx={(theme) => ({
            py: 1,
            px: 1.5,
            color: color(theme),
            '.Mui-checked': {
              color: color(theme),
            },
            '&.Mui-disabled': {
              color: color(theme),
            },
          })}
        />
      }
      label={label}
      value={option?._id || 'none'}
      sx={(theme) => ({
        '.MuiFormControlLabel-label': {
          color: color(theme),
        },
        '.MuiFormControlLabel-label.Mui-disabled': {
          color: color(theme),
        },
      })}
    />
  );
};

export default MultipleChoiceQuestionOptionPreview;
